import React, { forwardRef, useCallback } from 'react';
import Proptypes from 'prop-types';
import clsx from 'clsx';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';

const useStyles = makeStyles(theme => {
  return {
    icon: {
      top: 'calc(50% - 14.5px)',
      right: 3,
      color: props => (props.disabled ? theme.palette.common.muted : theme.palette.text.secondary),
    },
    paper: {
      boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.1)',
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
    menuList: {
      border: `1px solid ${theme.palette.border}`,
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
    errorSpace: {
      paddingBottom: theme.spacing(2),
    },
    underline: {
      '&:after': {
        borderBottomWidth: 1,
      },
      '&:before': {
        borderBottomColor: theme.palette.common.muted,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
      },
      '&.Mui-disabled:before': {
        borderBottomStyle: 'solid',
        borderBottomColor: theme.palette.text.disabled,
      },
      '&:hover:not(.Mui-disabled):before': {
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: theme.palette.text.secondary,
      },
    },
    // Start adornment filled styles
    startAdornmentRoot: {
      color: theme.palette.text.secondary,
    },
  };
});

const Select = forwardRef(function Select(
  {
    children,
    className,
    disabled,
    error,
    errorText,
    fullWidth,
    helpText,
    id,
    inputRef,
    label,
    name,
    onBlur,
    onChange,
    onClose,
    onOpen,
    required,
    title,
    value,
    startAdornment,
    ...restProps
  },
  ref
) {
  const { icon, paper, menuList, errorSpace, underline, startAdornmentRoot } = useStyles({
    disabled,
  });

  const handleOnChange = useCallback(event => onChange && onChange(event.target.value, event), [
    onChange,
  ]);

  const handleOnBlur = useCallback(event => onBlur && onBlur(event.target.value, event), [onBlur]);

  const selectProps = {
    classes: { icon },
    MenuProps: {
      classes: {
        paper,
      },
      PaperProps: {
        square: true,
      },
      getContentAnchorEl: null,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      MenuListProps: {
        className: menuList,
      },
    },
    onClose,
    onOpen,
  };

  const inputProps = { classes: { underline } };

  if (startAdornment) {
    inputProps.startAdornment = (
      <InputAdornment position="start" className={startAdornmentRoot}>
        {startAdornment}
      </InputAdornment>
    );
  }

  const inputLabelProps = { htmlFor: id };

  return (
    <TextField
      {...restProps}
      InputLabelProps={inputLabelProps}
      InputProps={inputProps}
      SelectProps={selectProps}
      className={clsx([className, errorText && !error && errorSpace])}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      helperText={(error && errorText) || helpText}
      inputRef={inputRef}
      label={label}
      margin="dense"
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      ref={ref}
      required={required}
      value={value}
      select
      id={id}
      name={name}
      title={title}
    >
      {children}
    </TextField>
  );
});

Select.propTypes = {
  children: Proptypes.node,
  className: Proptypes.string,
  disabled: Proptypes.bool,
  error: Proptypes.bool,
  errorText: Proptypes.string,
  fullWidth: Proptypes.bool,
  helpText: Proptypes.string,
  id: Proptypes.string.isRequired,
  inputRef: Proptypes.oneOfType([Proptypes.func, Proptypes.object]),
  label: Proptypes.string,
  name: Proptypes.string.isRequired,
  onBlur: Proptypes.func,
  onChange: Proptypes.func.isRequired,
  onClose: Proptypes.func,
  onOpen: Proptypes.func,
  required: Proptypes.bool,
  title: Proptypes.string,
  value: Proptypes.oneOfType([Proptypes.string, Proptypes.object, Proptypes.number]).isRequired, // NOTE: upstream uses any, this is safe to change too
  startAdornment: Proptypes.element,
};

Select.defaultProps = {
  children: null,
  className: '',
  disabled: false,
  error: false,
  errorText: '',
  fullWidth: true,
  helpText: '',
  inputRef: null,
  label: '',
  onBlur: null,
  onClose: null,
  onOpen: null,
  required: false,
  title: '',
  startAdornment: null,
};

Select.displayName = 'Select';

export default Select;
